'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import ReactPlayer from 'react-player';

interface Video {
  id: string;
  title: string;
  url: string;
}

interface VideoSliderProps {
  videos: Video[];
}

const VideoSlider: React.FC<VideoSliderProps> = ({ videos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [_direction, setDirection] = useState(0);
  const [visibleCount, setVisibleCount] = useState(1);
  const [playingVideos, setPlayingVideos] = useState<{
    [key: string]: boolean;
  }>({});

  // Calculate how many videos can fit based on screen width
  React.useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width >= 1200) { setVisibleCount(3); }
      else if (width >= 768) { setVisibleCount(2); }
      else { setVisibleCount(1); }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    // Pause all videos when changing slides
    setPlayingVideos({});
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex + newDirection * visibleCount;
      if (newIndex < 0) { return videos.length - visibleCount; }
      if (newIndex > videos.length - visibleCount) { return 0; }
      return newIndex;
    });
  };

  const visibleVideos = videos.slice(currentIndex, currentIndex + visibleCount);

  return (
    <div className='relative mx-auto w-full max-w-[1200px] px-4'>
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
        {visibleVideos.map((video, _index) => (
          <div key={video.id} className='flex h-full flex-col'>
            <h3 className='mb-3 line-clamp-2 h-[48px] text-center font-semibold text-lg text-white'>
              {video.title}
            </h3>
            <div className='relative aspect-[9/16] h-[400px] w-full overflow-hidden rounded-2xl bg-gray-900 shadow-2xl'>
              <ReactPlayer
                url={`https://www.youtube.com/shorts/${video.id}`}
                width="100%"
                height="100%"
                playing={playingVideos[video.id] || false}
                controls={true}
                config={{
                  youtube: {
                    playerVars: {
                      modestbranding: 1,
                      rel: 0,
                      showinfo: 0,
                      fs: 1,
                      playsinline: 1,
                    },
                  },
                }}
                onPlay={() =>
                  setPlayingVideos((prev) => ({ ...prev, [video.id]: true }))
                }
                onPause={() =>
                  setPlayingVideos((prev) => ({ ...prev, [video.id]: false }))
                }
                className="absolute inset-0"
              />
            </div>
          </div>
        ))}
      </div>

      <div className='mt-8 flex items-center justify-center gap-4'>
        <button
          onClick={() => paginate(-1)}
          className='rounded-full bg-black/50 p-2 backdrop-blur-sm transition-colors hover:bg-black/75'
        >
          <ChevronLeftIcon className='h-6 w-6 text-white' />
        </button>

        <div className="flex gap-2">
          {Array.from({ length: Math.ceil(videos.length / visibleCount) }).map(
            (_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex / visibleCount ? 1 : -1);
                  setPlayingVideos({});
                  setCurrentIndex(index * visibleCount);
                }}
                className={`h-2 w-2 rounded-full transition-colors ${
                  Math.floor(currentIndex / visibleCount) === index
                    ? 'bg-blue-500'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ),
          )}
        </div>

        <button
          onClick={() => paginate(1)}
          className='rounded-full bg-black/50 p-2 backdrop-blur-sm transition-colors hover:bg-black/75'
        >
          <ChevronRightIcon className='h-6 w-6 text-white' />
        </button>
      </div>
    </div>
  );
};

export default VideoSlider;
